import { useEffect, useState } from 'react';
import { peopleApi, OfficeMailboxes } from './api/people';

// The firms' office mailboxes (Admin → Companies → <firm> → Office mailbox), read from the server
// (GET /api/people/offices) — the client spells out no mailbox (multi-tenant, owner 2026-10-03).
// One request per page load, shared by every caller; empty until it has answered.
const EMPTY: OfficeMailboxes = { legal: '', accounting: '', all: [], calendars: [] };
let cache: OfficeMailboxes | null = null;
let pending: Promise<OfficeMailboxes> | null = null;
const listeners = new Set<(o: OfficeMailboxes) => void>();

export function loadOfficeMailboxes(): Promise<OfficeMailboxes> {
  if (cache) return Promise.resolve(cache);
  if (!pending) {
    pending = peopleApi.offices()
      .then(o => {
        cache = { legal: String(o?.legal || ''), accounting: String(o?.accounting || ''), all: (o?.all || []).map(String), calendars: (o?.calendars || []).map(String) };
        listeners.forEach(f => f(cache!));
        return cache;
      })
      .catch(() => { pending = null; return EMPTY; });   // retried by the next caller
  }
  return pending;
}
/** The office mailboxes now (EMPTY before the first answer) — for code outside a component. */
export const officeMailboxesNow = (): OfficeMailboxes => cache || EMPTY;
/** Is this one of the firms' office mailboxes? (false until the list has loaded) */
export const isOfficeMailbox = (e: any): boolean => (cache?.all || []).includes(String(e || '').trim().toLowerCase());

/** The office mailboxes, re-rendering the caller once they have loaded. */
export function useOfficeMailboxes(): OfficeMailboxes {
  const [o, setO] = useState<OfficeMailboxes>(cache || EMPTY);
  useEffect(() => {
    let live = true;
    const f = (x: OfficeMailboxes) => { if (live) setO(x); };
    listeners.add(f);
    void loadOfficeMailboxes().then(f);
    return () => { live = false; listeners.delete(f); };
  }, []);
  return o;
}
